<template>
	<modal
		class="topup-modal"
		size="medium"
		@close="$emit('close')"
		v-if="isVisible">
		<template #header>
			<h3>Topup</h3>
		</template>
		<template #body>
			<div class="callout callout--info mb-3 bg-blue-light">
				<p class="mb-0">{{ $t('Top up your account balance to continue using our services') }}.</p>
			</div>
			<!-- Amount -->
			<div class="form-group">
				<label class="text-bold">{{ $t('Topup Amount') }}</label>
				<price-display
					class="form-control"
					:class="{ 'is-error': topupAmount && topupAmount < minimumTopup }"
					placeholder="Enter amount"
					min="100000"
					v-model.number="topupAmount">
				</price-display>
				<small :class="topupAmount && topupAmount < minimumTopup ? 'text-danger' : 'text-muted'">
					Minimum topup: Rp 10.000
				</small>
				<div class="mt-2">
					<button type="button" class="btn btn-sm btn-outline-success mr-2" @click="topupAmount = 1000000">Rp 1.000.000</button>
					<button type="button" class="btn btn-sm btn-outline-success mr-2" @click="topupAmount = 500000">Rp 500.000</button>
					<button type="button" class="btn btn-sm btn-outline-success" @click="topupAmount = 100000">Rp 100.000</button>
				</div>
			</div>

			<!-- Payment Method -->
			<div class="form-group my-4">
				<label class="text-bold mb-3">{{ $t('Payment Method') }}</label>
				<div class="payment-methods">
					<div
						v-for="method in paymentMethods"
						:key="method.code"
						class="payment-method-card"
						:class="{ active: selectedPaymentMethod.code === method.code }"
						@click="selectedPaymentMethod = method">
						<div class="payment-method-icon">
							<template v-if="method.iconType === 'image'">
								<img :src="method.iconUrl" alt="Bank Icon" width="30" />
							</template>
							<i v-else :class="method.icon"></i>
						</div>
						<div class="payment-method-info">
							<small class="payment-method-name">{{ method.name }}</small>
						</div>
						<div class="payment-method-radio">
							<input
								type="radio"
								:value="method"
								v-model="selectedPaymentMethod"
								:id="`payment-${method.code}`">
						</div>
					</div>
				</div>
			</div>

			<!-- Fee Breakdown -->
			<div class="card bg-light">
				<div class="card-body">
					<h6 class="card-title text-bold mb-3">Rincian Pembayaran</h6>

					<!-- Topup Amount -->
					<div class="d-flex justify-content-between align-items-center mb-2">
						<span>Nilai Topup</span>
						<span class="text-bold">Rp {{ topupAmount ? topupAmount.toLocaleString('id-ID') : '0' }}</span>
					</div>

					<!-- Admin Fee -->
					<div class="d-flex justify-content-between align-items-center mb-2">
						<span>
							Admin Fee
							<small v-if="selectedPaymentMethod.code === 'gopay'" class="text-muted">
								{{ `(${selectedPaymentMethod.fee_value}% dari nilai topup)` }}
							</small>
						</span>
						<span class="text-bold">Rp {{ adminFee.toLocaleString('id-ID') }}</span>
					</div>

					<!-- Topup Fee -->
					<div class="d-flex justify-content-between align-items-center mb-3">
						<span>Topup Fee</span>
						<span class="text-bold">Rp {{ topupFee.toLocaleString('id-ID') }}</span>
					</div>

					<!-- Total -->
					<div class="border-top pt-3">
						<div class="d-flex justify-content-between align-items-center">
							<span class="text-bold" style="font-size: 1.1em;">Total Pembayaran</span>
							<span class="text-success" style="font-size: 1.3em; font-weight: bold;">
								Rp {{ totalPayment.toLocaleString('id-ID') }}
							</span>
						</div>
					</div>
				</div>
			</div>
		</template>
		<template #footer>
			<!-- Cancel -->
			<button
				type="button"
				class="btn btn-light"
				@click="$emit('close')">
				{{ $t('Cancel') }}
			</button>
			<!-- Submit -->
			<button
				class="btn btn-primary"
				:class="{ 'is-loading': isProcessingTopup }"
				:disabled="!topupAmount || topupAmount < minimumTopup || isProcessingTopup"
				@click="processTopup">
				<span>{{ $t('Proceed to Payment') }}</span>
			</button>
		</template>
	</modal>
</template>

<script>
import Modal from '@/components/Modal.vue';
import PriceDisplay from '@/components/PriceDisplay.vue';
import { getAxiosErrorMessage, showMidtransSnap } from '@/lib/helper';
import Wallet from '@/api/wallet';
import { TOPUP_FEE, MINIMUM_TOPUP } from '@/lib/statics';

const paymentMethods = [
	{
		code: 'bca_va',
		name: 'BCA VA',
		description: 'Pay using BCA Virtual Account',
		icon: 'fas fa-university',
		iconType: 'image',
		iconUrl: 'https://cdn.ostock.id/img/banks/bank-bca.png',
		fee_type: 'fixed',
		fee_value: 5000, // Rp 5.000 fee
	},
	{
		code: 'bni_va',
		name: 'BNI VA',
		description: 'Pay using BNI Virtual Account',
		icon: 'fas fa-university',
		iconType: 'image',
		iconUrl: 'https://cdn.ostock.id/img/banks/bank-bni.png',
		fee_type: 'fixed',
		fee_value: 5000, // Rp 5.000 fee
	},
	{
		code: 'bri_va',
		name: 'BRI VA',
		description: 'Pay using BRI Virtual Account',
		icon: 'fas fa-university',
		iconType: 'image',
		iconUrl: 'https://cdn.ostock.id/img/banks/bank-bri.png',
		fee_type: 'fixed',
		fee_value: 5000, // Rp 5.000 fee
	},
	{
		code: 'echannel',
		name: 'Mandiri Bill',
		description: 'Pay using Mandiri Bill',
		icon: 'fas fa-university',
		iconType: 'image',
		iconUrl: 'https://cdn.ostock.id/img/banks/bank-mandiri.png',
		fee_type: 'fixed',
		fee_value: 5000, // Rp 5.000 fee
	},
	{
		code: 'permata_va',
		name: 'Permata VA',
		description: 'Pay using Permata Virtual Account',
		icon: 'fas fa-university',
		iconType: 'image',
		iconUrl: 'https://cdn.ostock.id/img/banks/bank-permata.png',
		fee_type: 'fixed',
		fee_value: 5000, // Rp 5.000 fee
	},
	{
		code: 'gopay',
		name: 'QRIS',
		description: 'Transfer via ATM, Internet Banking, or Mobile Banking',
		icon: 'fas fa-qrcode',
		iconType: 'font',
		fee_type: 'percentage',
		fee_value: 0.7, // 0.7% fee
	},
];

export default {
	name: 'TopupModal',
	components: {
		Modal,
		PriceDisplay,
	},
	props: {
		isVisible: {
			type: Boolean,
			default: false,
		},
		initialAmount: {
			type: Number,
			default: 500000,
		},
	},
	data() {
		return {
			topupAmount: 500000,
			minimumTopup: MINIMUM_TOPUP,
			isProcessingTopup: false,
			paymentMethods,
			selectedPaymentMethod: paymentMethods[0],
		};
	},
	computed: {
		adminFee() {
			const paymentMethodFee = this.calculateFeeByPaymentMethod(this.selectedPaymentMethod);
			return paymentMethodFee;
		},
		topupFee() {
			return TOPUP_FEE;
		},
		totalPayment() {
			return this.topupAmount + this.adminFee + this.topupFee;
		},
	},
	watch: {
		isVisible(newVal) {
			if (newVal) {
				// Use initialAmount if provided, otherwise default to 500000
				this.topupAmount = this.initialAmount;
			}
		},
	},
	methods: {
		processTopup() {
			if (!this.topupAmount || this.topupAmount < this.minimumTopup) {
				return;
			}

			this.isProcessingTopup = true;
			const selectedPaymentMethod = this?.selectedPaymentMethod?.code || this.paymentMethods[0].code;
			const params = {
				amount: this.topupAmount,
				payment_method: selectedPaymentMethod,
			};

			const callback = (response) => {
				this.isProcessingTopup = false;
				this.$emit('close');

				const data = response.data;

				// Show Midtrans Snap
				if (data && data.payment_token) {
					showMidtransSnap(data.payment_token, this.midtransCallback, selectedPaymentMethod);
					// Reset form
					this.topupAmount = 500000;
				}
			};

			const errorCallback = (e) => {
				this.isProcessingTopup = false;
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Topup Request Failed',
					text: message,
				});
			};

			Wallet.topup(params, callback, errorCallback);
		},
		midtransCallback(status, result) {
			console.log(result);
			if (status === 'success') {
				console.log(status, result);
				this.$emit('success');
			} else if (status === 'pending') {
				const pdfUrl = result.pdf_url;
				console.log(status, pdfUrl);
				window.open(pdfUrl, '_blank');
				this.closeSnapMidtrans();
			} else if (status === 'error') {
				console.log(status);
			} else if (status === 'close') {
				console.log(status);
			}
		},
		closeSnapMidtrans() {
			if (window.snap) {
				window.snap.hide();
			}
		},
		calculateFeeByPaymentMethod(selectedPaymentMethod) {
			if (!selectedPaymentMethod) return 0;

			if (selectedPaymentMethod.fee_type === 'fixed') {
				return selectedPaymentMethod.fee_value;
			}
			if (selectedPaymentMethod.fee_type === 'percentage') {
				return Math.ceil((this.topupAmount * selectedPaymentMethod.fee_value) / 100);
			}

			return 0;
		},
	},
};
</script>
<style lang="scss" scoped>
.payment-methods {
	// display: grid;
	// grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
	// gap: 12px;
	display: flex;
	gap: 7px;
    flex-wrap: wrap;
}

.payment-method-card {
	display: flex;
	// font-size: 12px;
	align-items: center;
	padding: 5px 8px;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.3s ease;
	background-color: #fff;
}

.payment-method-card:hover {
	border-color: #006DC7;
	background-color: #dceefd;
}

.payment-method-card.active {
	border-color: #006DC7;
	background-color: #dceefd;
	box-shadow: 0 2px 8px rgba(0, 109, 199, 0.15);
}

.payment-method-icon {
	font-size: 18px;
	margin-right: 8px;
	// min-width: 30px;
	text-align: center;
	color: #006DC7;
	display: flex;
    align-items: center;
}

.payment-method-info {
	flex: 1;
	margin-right: 10px;
}

.payment-method-name {
	margin: 0;
	font-weight: 600;
	font-size: 12px;
	color: #333;
}

.payment-method-desc {
	margin: 4px 0 0;
	color: #999;
	display: block;
}

.payment-method-radio {
	flex-shrink: 0;
	display: none;
}

.payment-method-radio input {
	cursor: pointer;
}
</style>
